import React, { useEffect, useMemo, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import {
    Loader2, Plus, RefreshCw, ArrowLeftRight, FileText,
    TrendingUp, AlertTriangle,
} from "lucide-react";

const KINDS = [
    { id: "allocation", label: "Allocation", hint: "Work assigned to this agent." },
    { id: "adjustment", label: "Adjustment", hint: "Write-up or write-down on past work." },
    { id: "transaction", label: "Transaction", hint: "Value moved to / from another agent. Both numbers required (Rule 8)." },
    { id: "note", label: "Note", hint: "Amountless record. Decision, flag, context." },
];

function fmt(n) {
    const num = Number(n) || 0;
    return (num >= 0 ? "+" : "") + num.toFixed(2);
}

function StatusPill({ value }) {
    const tone = {
        "Permanent": "bg-ink text-paper-light",
        "Probationary": "border border-stamp-red text-stamp-red",
        "Active": "border border-ink/40 text-ink",
        "Seasonal": "border border-dashed border-ink/40 text-ink-soft",
        "Variable": "border border-dashed border-stamp-red text-stamp-red",
    }[value] || "border border-ink/30 text-ink";
    return (
        <span className={`inline-block px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${tone}`}>
            {value}
        </span>
    );
}

function AccountsTable({ accounts, selected, onSelect }) {
    return (
        <div className="paper-card tape-corner p-4 overflow-x-auto" data-testid="spyfin-accounts-card">
            <table className="w-full" data-testid="spyfin-accounts-table">
                <thead>
                    <tr className="border-b-2 border-ink/30 text-left">
                        <th className="py-2 pr-2 label-typed">No.</th>
                        <th className="py-2 pr-2 label-typed">Agent</th>
                        <th className="py-2 pr-2 label-typed">Status</th>
                        <th className="py-2 pr-2 label-typed text-right">Balance</th>
                        <th className="py-2 pr-2 label-typed text-right">Entries</th>
                    </tr>
                </thead>
                <tbody>
                    {accounts.map((a) => {
                        const active = selected === a.agent_key;
                        return (
                            <tr
                                key={a.agent_key}
                                onClick={() => onSelect(a.agent_key)}
                                className={`border-b border-ink/10 cursor-pointer ${active ? "bg-stamp-red/10" : "hover:bg-paper-light/40"}`}
                                data-testid={`spyfin-row-${a.agent_key}`}
                            >
                                <td className="py-2 pr-2 font-heading text-stamp-red text-sm tracking-[0.05em] whitespace-nowrap">
                                    #{a.agent_number}
                                </td>
                                <td className="py-2 pr-2">
                                    <div className="font-heading uppercase text-xs text-ink tracking-[0.05em]">{a.agent_name}</div>
                                    <div className="label-typed text-[9px]">{a.agent_alias}</div>
                                </td>
                                <td className="py-2 pr-2"><StatusPill value={a.status} /></td>
                                <td className={`py-2 pr-2 text-right font-heading text-sm tracking-[0.05em] ${a.balance > 0 ? "text-stamp-green" : a.balance < 0 ? "text-stamp-red" : "text-ink-soft"}`}>
                                    {fmt(a.balance)}
                                </td>
                                <td className="py-2 pr-2 text-right font-body text-xs text-ink-soft">{a.entry_count}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function FileEntryForm({ accounts, current, onFiled }) {
    const [kind, setKind] = useState("allocation");
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [related, setRelated] = useState("");
    const [ref, setRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        setErr("");
        if (!description.trim()) { setErr("Description is required."); return; }
        if (kind === "transaction" && !related) { setErr("Transactions require both agent numbers (Rule 8)."); return; }
        setBusy(true);
        try {
            await api.post("/spyfinance/ledger", {
                agent_number: current,
                kind,
                amount: kind === "note" ? 0 : Number(amount || 0),
                description,
                related_agent_number: kind === "transaction" ? related : null,
                ref: ref || null,
            });
            setAmount(""); setDescription(""); setRelated(""); setRef("");
            onFiled();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const kindHint = KINDS.find((k) => k.id === kind)?.hint;

    return (
        <div className="paper-card border-2 border-dashed border-ink/30 p-4" data-testid="spyfin-file-form">
            <div className="label-typed mb-3">File entry · No.{current}</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
                {KINDS.map((k) => (
                    <button
                        key={k.id}
                        onClick={() => setKind(k.id)}
                        data-testid={`spyfin-kind-${k.id}`}
                        className={`border px-3 py-2 font-heading uppercase text-[10px] tracking-[0.22em] text-left ${kind === k.id ? "bg-ink text-paper-light border-ink" : "border-ink/30 text-ink hover:border-stamp-red"}`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>
            <div className="label-typed mb-2 text-[9px] italic">{kindHint}</div>

            {kind !== "note" && (
                <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Amount (signed; +credit / -debit)"
                    data-testid="spyfin-amount"
                    className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 mb-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
            )}
            <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description — what is this entry?"
                data-testid="spyfin-description"
                rows={2}
                className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 mb-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
            />
            {kind === "transaction" && (
                <select
                    value={related}
                    onChange={(e) => setRelated(e.target.value)}
                    data-testid="spyfin-related"
                    className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 mb-2 font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                >
                    <option value="">— with No. (required) —</option>
                    {accounts
                        .filter((a) => a.agent_number !== current)
                        .map((a) => (
                            <option key={a.agent_number} value={a.agent_number}>
                                No.{a.agent_number} · {a.agent_name}
                            </option>
                        ))}
                </select>
            )}
            <input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="Optional reference (RFI / PR / ticket)"
                data-testid="spyfin-ref"
                className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 mb-3 font-body text-xs text-ink-soft placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
            />
            {err && (
                <div className="border border-stamp-red bg-stamp-red/5 px-3 py-2 mb-2 text-xs text-stamp-red flex items-center gap-2" data-testid="spyfin-form-error">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>{err}</span>
                </div>
            )}
            <button
                onClick={submit}
                disabled={busy}
                data-testid="spyfin-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                File entry
            </button>
            <div className="mt-2 label-typed text-[9px] italic text-center">
                Append-only · originals never altered (Rule 1)
            </div>
        </div>
    );
}

function AccountDetail({ data, refresh }) {
    if (!data) {
        return (
            <div className="paper-card p-10 text-center text-ink-soft label-typed tilt-r" data-testid="spyfin-empty-detail">
                Select an agent on the left to open the file.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="spyfin-detail">
            <div className="paper-card p-5 relative">
                <span className="absolute top-3 right-4 classified-stamp text-[10px]">Account File</span>
                <div className="label-typed">No. {data.agent_number} · {data.agent_alias}</div>
                <h2 className="font-heading text-3xl uppercase tracking-[0.04em] text-ink mt-1">{data.agent_name}</h2>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                        <div className="label-typed">Status</div>
                        <div className="font-heading text-sm text-ink mt-1">{data.status}</div>
                    </div>
                    <div>
                        <div className="label-typed">Trusted?</div>
                        <div className="font-heading text-sm text-ink mt-1">{data.trusted}</div>
                    </div>
                    <div>
                        <div className="label-typed">Active?</div>
                        <div className="font-heading text-sm text-ink mt-1">{data.active}</div>
                    </div>
                    <div>
                        <div className="label-typed">Last used</div>
                        <div className="font-heading text-sm text-ink mt-1">{data.last_used}</div>
                    </div>
                </div>
                <div className="mt-4 pt-3 border-t border-ink/15 font-body text-sm text-ink leading-relaxed">
                    {data.role}
                </div>
                <div className="mt-4 pt-3 border-t border-ink/15 flex items-end justify-between">
                    <div>
                        <div className="label-typed">Fractional balance</div>
                        <div className={`font-heading text-4xl tracking-[0.04em] ${data.balance > 0 ? "text-stamp-green" : data.balance < 0 ? "text-stamp-red" : "text-ink"}`} data-testid="spyfin-balance">
                            {fmt(data.balance)} <span className="text-base font-body text-ink-soft">units</span>
                        </div>
                    </div>
                    <div className="text-right label-typed">
                        {data.entries.length} entries on file
                    </div>
                </div>
            </div>

            <FileEntryForm accounts={data._accounts || []} current={data.agent_number} onFiled={refresh} />

            <div className="paper-card p-5">
                <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink mb-3">Ledger · most recent first</div>
                {data.entries.length === 0 ? (
                    <div className="font-body text-sm text-ink-faint italic">No entries filed yet.</div>
                ) : (
                    <ul className="divide-y divide-ink/10">
                        {data.entries.map((e) => (
                            <li key={e.id} className="py-3" data-testid={`spyfin-entry-${e.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-0.5 border ${e.kind === "transaction" ? "border-stamp-red text-stamp-red" : e.kind === "allocation" ? "border-stamp-green text-stamp-green" : e.kind === "adjustment" ? "border-ink/40 text-ink" : "border-dashed border-ink/30 text-ink-soft"}`}>
                                                {e.kind === "transaction" && <ArrowLeftRight className="w-3 h-3 inline -mt-0.5 mr-1" />}
                                                {e.kind === "allocation" && <TrendingUp className="w-3 h-3 inline -mt-0.5 mr-1" />}
                                                {e.kind === "note" && <FileText className="w-3 h-3 inline -mt-0.5 mr-1" />}
                                                {e.kind}
                                            </span>
                                            {e.kind !== "note" && (
                                                <span className={`font-heading text-sm ${e.amount > 0 ? "text-stamp-green" : e.amount < 0 ? "text-stamp-red" : "text-ink-soft"}`}>
                                                    {fmt(e.amount)} {e.currency}
                                                </span>
                                            )}
                                            {e.related_agent_number && (
                                                <span className="label-typed text-[9px]">↔ No.{e.related_agent_number}</span>
                                            )}
                                            {e.ref && <span className="label-typed text-[9px]">{e.ref}</span>}
                                        </div>
                                        <div className="font-body text-sm text-ink mt-1">{e.description}</div>
                                        <div className="label-typed mt-1 text-[9px]">
                                            {e.filed_at?.replace("T", " ").slice(0, 19)} · filed by {e.filed_by}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function SpyFinance() {
    const [accounts, setAccounts] = useState([]);
    const [agentCount, setAgentCount] = useState(0);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tick, setTick] = useState(0);

    const refresh = () => setTick((t) => t + 1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/spyfinance/accounts");
                if (cancelled) return;
                setAccounts(data.accounts || []);
                setAgentCount(data.agent_count || 0);
                if (!selected && data.accounts?.length) setSelected(data.accounts[0].agent_key);
            } catch (e) {
                setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [tick]);

    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/spyfinance/accounts/${encodeURIComponent(selected)}`);
                if (!cancelled) setDetail({ ...data, _accounts: accounts });
            } catch (e) {
                if (!cancelled) setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
            }
        })();
        return () => { cancelled = true; };
    }, [selected, accounts, tick]);

    const totals = useMemo(() => {
        const positive = accounts.filter((a) => a.balance > 0).length;
        const negative = accounts.filter((a) => a.balance < 0).length;
        const flat = accounts.filter((a) => a.balance === 0).length;
        const ledger = accounts.reduce((s, a) => s + a.entry_count, 0);
        return { positive, negative, flat, ledger };
    }, [accounts]);

    return (
        <Shell>
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Separate entity · Canon Rule 6 · Three holds the detail</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                        Spy Finance<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Fractional accounts, one per numbered agent. Append-only ledger,
                        signed amounts, transactions carry both numbers (Rule 8). Currency
                        is intentionally generic — units — until No. 6 names a basis.
                    </p>
                </div>
                <button
                    onClick={refresh}
                    className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.25em]"
                    data-testid="spyfin-refresh"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="spyfin-stats">
                <div className="paper-card p-3">
                    <div className="label-typed">Agents on file</div>
                    <div className="font-heading text-2xl text-ink">{agentCount}</div>
                </div>
                <div className="paper-card p-3">
                    <div className="label-typed">Ledger entries</div>
                    <div className="font-heading text-2xl text-ink">{totals.ledger}</div>
                </div>
                <div className="paper-card p-3">
                    <div className="label-typed">In credit / in debit</div>
                    <div className="font-heading text-2xl">
                        <span className="text-stamp-green">{totals.positive}</span>
                        <span className="text-ink-soft mx-1">/</span>
                        <span className="text-stamp-red">{totals.negative}</span>
                    </div>
                </div>
                <div className="paper-card p-3">
                    <div className="label-typed">Flat / opening</div>
                    <div className="font-heading text-2xl text-ink-soft">{totals.flat}</div>
                </div>
            </div>

            {err && (
                <div className="paper-card border-2 border-stamp-red p-4 mb-6" data-testid="spyfin-error">
                    <div className="flex items-center justify-between">
                        <span className="stamp-red font-heading text-sm tracking-[0.2em]">FAILURE ON FILE</span>
                        <button onClick={() => setErr("")} className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft hover:text-stamp-red">clear</button>
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words mt-2">{err}</pre>
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 text-ink-soft label-typed">
                    <Loader2 className="w-4 h-4 animate-spin" /> Reading accounts...
                </div>
            ) : (
                <div className="grid lg:grid-cols-5 gap-6">
                    <div className="lg:col-span-2">
                        <AccountsTable accounts={accounts} selected={selected} onSelect={setSelected} />
                    </div>
                    <div className="lg:col-span-3">
                        <AccountDetail data={detail} refresh={refresh} />
                    </div>
                </div>
            )}
        </Shell>
    );
}
